import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs';
import { CallbackLoadDataGridActionHandler } from '../../../../../components/common/grid-base/actions/callback-load-data-grid-action-handler';
import { ExcelExportGridActionHandler } from '../../../../../components/common/grid-base/actions/excel-export-grid-action-handler';
import { GridActionHandlerBase } from '../../../../../components/common/grid-base/actions/grid-action-handler-base';
import { HideLegacyLoaderGridActionHandler } from '../../../../../components/common/grid-base/actions/hide-legacy-loader-grid-action-handler';
import { LoadDataGridActionHandler } from '../../../../../components/common/grid-base/actions/load-data-grid-action-handler';
import { LoadDataKeepFilterGridActionHandler } from '../../../../../components/common/grid-base/actions/load-data-keep-filter-grid-action-handler';
import { LoadDataKeepFilterKeepSelectionGridActionHandler } from '../../../../../components/common/grid-base/actions/load-data-keep-filter-keep-selection-grid-action-handler';
import { SearchGridActionHandler } from '../../../../../components/common/grid-base/actions/search-grid-action-handler';
import { ShowAllGridActionHandler } from '../../../../../components/common/grid-base/actions/show-all-grid-action-handler';
import { UnselectCheckboxesGridActionHandler } from '../../../../../components/common/grid-base/actions/unselect-checkboxes-grid-action-handler';
import { TabConfig } from '../../../../../components/common/pollux-tab-config/pollux-form-config';
import { WorkplaceBase } from '../../../../../components/common/workplace-base/workplace-base';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { RaciNavigationService } from '../../../../../navigation/raci-navigation.service';
import { AppLoadingIndicatorService } from '../../../../../services/app-loading-indicator.service';
import { CopyPortfolioGridActionHandler } from './actions/copy-portfolio-grid-action-handler';
import { CreateReportFromValuationEventGridActionHandler } from './actions/create-report-from-valuation-event-grid-action-handler';
import { CreateReportsGridActionHandler } from './actions/create-reports-grid-action-handler';
import { DeletePortfolioGridActionHandler } from './actions/delete-portfolio-grid-action-handler';
import { ExportPortfolioToArgusGridActionHandler } from './actions/export-portfolio-to-argus-grid-action-handler';
import { FinalizePortfolioGridActionHandler } from './actions/finalize-portfolio-grid-action-handler';
import { GetAllSelectedPortfoliosGridActionHandler } from './actions/get-all-selected-portfolios-grid-action-handler';
import { PrintPortfolioGridActionHandler } from './actions/print-portfolio-grid-action-handler';
import { PublishUnpublishPortfolioGridActionHandler } from './actions/publish-unpublish-portfolio-grid-action-handler';
import { StorageSetSelectedPortfolioGridActionHandler } from './actions/storage-set-selected-portfolio-grid-action-handler';
import { UnFinalizePortfolioGridActionHandler } from './actions/unfinalize-portfolio-grid-action-handler';

/**
 * Workspace for Valuation Events
 */
@Component({
  selector: 'valuation-events-workspace',
  templateUrl: './valuation-events-workspace.component.html',
  styleUrls: ['./valuation-events-workspace.component.css']
})
export class ValuationEventsWorkspaceComponent extends WorkspaceBase implements OnInit {
  /**
   * JavaScript Oid property name of BO (in this case Project DTO)
   */
  public oidPropertyName = "Oid";

  /**
   * Show the properties pane?
   */
  public showPropertiesPane = false;

  /**
   * Show the participants pane?
   */
  public showParticipantsPane = false;

  /**
   * Show the currencies pane?
   */
  public showCurrenciesPane = false;

  /**
   * Show import?
   */
  public showImport = false;

  /**
   * Import data
   */
  public importData: any = null;

  /**
   * Show export?
   */
  public showExport = false;

  /**
   * Export data
   */
  public exportData: any = null;

  /** The parent*/
  public parent: any = null;

  /**
   * Child mode?
   */
  public childMode = false;

  /**
   * Show the edit form
   * @param oid
   */
  public showForm(oid: number): void {
    this.resetGridUIState();

    this.showGrid = false;
    this.oid = oid;

    this.eventBus.broadcastEvent('searchBarVisibility', false);
  }

  /**
   * Show the edit form with parent selected
   * @param oid
   * @param parent
   * @param childMode
   */
  public showFormWithParent(oid: number, parent: any, childMode: boolean): void {
    this.resetGridUIState();
    this.showGrid = false;
    this.oid = oid;
    this.childMode = childMode;
    this.parent = parent;

    this.eventBus.broadcastEvent('searchBarVisibility', false);
  }

  /**
   * The workplace
   */
  private _workplace: WorkplaceBase;

  /**
   * Sets the workplace
   */
  @Input() public set workplace(workplace: WorkplaceBase) {
    this._workplace = workplace;
  }

  /**
   * Gets the workplace
   */
  public get workplace(): WorkplaceBase {
    return this._workplace;
  }

  /**
   * The tab element
   */
  private _tabElement: TabConfig;

  /**
   * Keep filter flag
   */
  private _keepFilter = false;

  /**
   * Sets te tab element
   */
  @Input() public set tabElement(tabElement: TabConfig) {
    this._tabElement = tabElement;
  }

  /**
   * Gets the tab element
   */
  public get tabElement(): TabConfig {
    return this._tabElement;
  }

  /**
   * Constructor
   * @param injector
   * @param appLoadingIndicator
   * @param _raciNavigationService
   */
  constructor(protected injector: Injector,
    private appLoadingIndicator: AppLoadingIndicatorService,
    private _raciNavigationService: RaciNavigationService) {
    super(injector);
  }

  /**
   * Called if the grid state was changed. Use this to notify the nav bar or perform similar actions
   * @param gridState The new grid state: true: Grid is shown, so the form isn't showh. False: Grid isn't show, but the form is shown
   */
  protected onShowGridChanged(gridState: boolean): void {
    this.eventBus.broadcastEvent('showGridChanged', { area: 'projectValuationPortfolio', value: gridState });
  }

  /**
   * Close all panes
   */
  public closeAllPanes(): void {
    this.resetGridUIState();
    this._raciNavigationService.onUpdateStepsIcon(true);
  }

  /**
   * Gets all action handlers
   */
  protected get actionHandlers(): GridActionHandlerBase[] {
    return [
      new ExcelExportGridActionHandler(this.injector, this, 'exportToExcelPortfoliosGrid'),
      new SearchGridActionHandler(this.injector, this, 'project.valuation.search'),
      new ShowAllGridActionHandler(this.injector, this, 'project.valuation.showAll'),
      new LoadDataGridActionHandler(this.injector, this, 'PortfolioLoadData'),
      new CallbackLoadDataGridActionHandler(this.injector, this, 'valuationPortfolioLoadData'),
      new LoadDataKeepFilterKeepSelectionGridActionHandler(this.injector, this, 'valuationPortfolioLoadDataFromPublish'),
      new CallbackLoadDataGridActionHandler(this.injector, this, 'valuationProjectProjectstdItemSavedReloadPortfolio'),

      new LoadDataKeepFilterGridActionHandler(this.injector, this, 'PortfolioLoadDataKeepFilter'),

      new PublishUnpublishPortfolioGridActionHandler(this.injector, this, 'publishPortfolio', 'publish'),
      new PublishUnpublishPortfolioGridActionHandler(this.injector, this, 'unpublishPortfolio', 'unpublish'),
      new PublishUnpublishPortfolioGridActionHandler(this.injector, this, 'republishPortfolio', 're-publish'),
      new CreateReportsGridActionHandler(this.injector, this, this.appLoadingIndicator),
      new PrintPortfolioGridActionHandler(this.injector, this),
      new CopyPortfolioGridActionHandler(this.injector, this),
      new ExportPortfolioToArgusGridActionHandler(this.injector, this, this.appLoadingIndicator),
      new FinalizePortfolioGridActionHandler(this.injector, this, this.appLoadingIndicator),
      new UnFinalizePortfolioGridActionHandler(this.injector, this),

      new DeletePortfolioGridActionHandler(this.injector, this),
      new StorageSetSelectedPortfolioGridActionHandler(this.injector, this),
      new GetAllSelectedPortfoliosGridActionHandler(this.injector, this),
      new LoadDataKeepFilterGridActionHandler(this.injector, this, 'valuationProjectPortfolioItemSaved'),
      new UnselectCheckboxesGridActionHandler(this.injector, this),
      new HideLegacyLoaderGridActionHandler(this.injector, this, 'argusIntegrationCompleted', this.appLoadingIndicator),
      new CreateReportFromValuationEventGridActionHandler(this.injector, this),
      new LoadDataKeepFilterGridActionHandler(this.injector, this, 'importSuccessLoadData')
    ];
  }

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected registerEvents(): void {
    window.addEventListener("beforeunload", () => {
      sessionStorage.removeItem('valuationFilter');
      sessionStorage.removeItem('valuationPageSize');
      sessionStorage.removeItem('valuationPaging');
      sessionStorage.removeItem('raciValuationEventId');
    });

    this.onEvents.push(this.eventBus.subscribeEvent('closePortfolioForm', () => {
      this.showGrid = true;
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', false);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationPortfoliosDataSaved', (elem) => {
      this.showGrid = true;
      this.eventBus.broadcastEvent('PortfolioLoadDataKeepFilter', elem);
    }));


    this.onEvents.push(this.eventBus.subscribeEvent('reportDeleteLoadData', () => {
      this._keepFilter = true;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportPrintLoadData', () => {
      this._keepFilter = true;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('newValPortfolioFromNav', () => {
      this.oid = null;
      setTimeout(() => {
        this.showForm(-1);
        this.eventBus.broadcastEvent('hideImportNavigationActions');
        this.eventBus.broadcastEvent('buttonNewPortfolioFromProjectStd');
      });
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('createValuationPortfoliosData', (parent) => {
      this.oid = null;
      setTimeout(() => {
        this.showFormWithParent(-1, parent, false);
        this.eventBus.broadcastEvent('hideImportNavigationActions');
      });
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('newChildValPortfolioFromNav', () => {
      this.oid = null;
      setTimeout(() => {
        this.showFormWithParent(-1, this.grid.selectedItem, true);
        this.eventBus.broadcastEvent('hideImportNavigationActions');
      });
    }));


    this.onEvents.push(this.eventBus.subscribeEvent('newPortfolioClick', () => {
      this.eventBus.broadcastEvent('createValuationPortfoliosData');
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('backClickedTrue', () => { this.resetGridUIState(); }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', (tab) => {
      if (tab !== 'tabValuationPortfolio') {
        this.resetGridUIState();
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('importFile', () => {
      this.importFiles(true);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('importWizard', () => {
      this.showImport = true;
      this.eventBus.broadcastEvent('hideImportNavigationActions');
      this.importFiles(false);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('exportWizard', () => {
      this.showExport = true;
      this.exportFiles();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('onImportCancel', () => {
      this.showImport = false;
      this.importData = null;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('onExportCancel', () => {
      this.showExport = false;
      this.exportData = null;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('onImportSuccess', (oid) => {
      this.checkFunction(oid).then((checkResult) => {
        if (checkResult?.passed) {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'importFlatfile.success');
        }
        else if (checkResult && !checkResult.passed) {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: checkResult.okFunction,
            cancel: checkResult.cancelFunction
          }, checkResult.translationKey);
        }
      });
      setTimeout(() => {
        this.eventBus.broadcastEvent('importSuccessLoadData');
      }, 10);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('showPortfolioProperties', () => {
      this.showPropertiesPane = true;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('showPortfolioParticipants', () => {
      this.showParticipantsPane = true;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('showCurrenciesPane', () => {
      this.showCurrenciesPane = true;
    }));
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  private getCheckYieldsAfterImportUrl(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/checkYieldsAfterImport?oid=${oid}`);
  }

  /**
   * Check function after the import
   * @param oid
   * @returns
   */
  private checkFunction(oid): Promise<any> {
    return new Promise<any>((resolve) => {
      const url = this.getCheckYieldsAfterImportUrl(oid);

      this.http.get(url).pipe(take(1)).subscribe((checkResult: any) => {
        resolve({
          passed: checkResult.success,
          translationKey: 'importFlatfile.importV2DataMissing',
          okFunction: () => {
            this.showGrid = false;
            this.showImport = false;
            this.eventBus.broadcastEvent('hideImportNavigationActions');
            this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);

            this.showForm(oid);
          },
          cancelFunction: () => {
            //Nothing. Just show the cancel button
          }
        });
      });
    });
  }

  /**
   * Import files
   * @param displayImportActions
   * @returns
   */
  private importFiles(displayImportActions) {
    if (this.grid.selectedItem) {
      if (this.grid.selectedItem.isFinalized) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            //Nothing
          }
        }, 'portfolioAlreadyFinalized');
        return;
      }
      else {
        this.importData = { oid: this.grid.selectedItem.Oid, projectId: this.grid.selectedItem.ProjectID }
        this.showImport = true;
        this.showExport = true;
        this.eventBus.broadcastEvent('displayImportActions', displayImportActions);
      }
    }
    else {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'errorMessageChoosePortfolio');
    }
  }

  /**
   * Export files
   */
  private exportFiles(): void {
    if (this.grid.selectedItem) {
      this.exportData = { oid: this.grid.selectedItem.Oid, projectId: this.grid.selectedItem.ProjectID }
      this.showExport = true;
    }
    else {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'errorMessageChoosePortfolio');
    }
  }

  /**
   * Reset the UI state
   */
  private resetGridUIState(): void {
    this.showGrid = true;
    this.oid = null;
    this.childMode = false;
    this.parent = null;
    this.showExport = false;
    this.exportData = null;
    this.showImport = false;
    this.importData = null;
    this.showPropertiesPane = false;
    this.showParticipantsPane = false;
    this.showCurrenciesPane = false;
  }

  /**
   * Applies a filter if an element was selected in a higher hierarchy area
   * @param item Item
   * @param selectedHierarchy Hierarchy where the element was selected
   * @param tabConfig Tab config
   * @param reset Reset the selection in the current area
   */
  public filter(item: any, selectedHierarchy: number, tabConfig: any, reset: boolean): void {
    if (reset) {
      this.grid.loadData({
        skip: 0
      }, null);
      return;
    }
    if (selectedHierarchy != null) {
      const filteredTab = tabConfig[selectedHierarchy];
      if (filteredTab.searchMode === 'projectstd') {
        this.grid.loadData({
          skip: 0,
          filter: {
            logic: "and",
            filters: [{ field: "ProjectID", operator: "eq", value: item.Oid }]
          }
        }, null);
      }
    }
    else {
      if (!this._keepFilter) {
        this.grid.loadData({
          skip: 0
        }, null);
      }
      this._keepFilter = false;
    }
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.init();

    this.workplace.onTabCreated(this);
  }
}
